import { Stack, Typography } from '@mui/material';
import {
    AccessTimeRounded,
    PaymentsOutlined,
    ScheduleRounded,
} from '@mui/icons-material';
import {
    type GetProductsResItem0PricingItemDto,
    type GetProductsResItem0ScheduleItemDto,
    ProductType,
    type TimeOffset,
} from '@travelity/api/generated';
import { Tag } from '@travelity/ui/tag';
import { SquareCard } from '@travelity/ui/square-card';
import { Card } from '@travelity/ui/card';
import { IconButton } from '@travelity/ui/icon-button';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { IconButtonGroup } from '@travelity/ui/icon-button-group';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatTimeOffset } from '@/utils/formatTimeOffset';
import { getMinPriceFromDualCurrencyItems } from '@/utils/getMinPriceFromDualCurrencyItems';

interface PackageProductItemProps {
    productId: string;
    productName: string;
    productType: ProductType;
    startTime: TimeOffset;
    pricingItems?: GetProductsResItem0PricingItemDto[];
    schedules?: GetProductsResItem0ScheduleItemDto[];
}

export const PackageProductItem = ({
    productId,
    productName,
    productType,
    startTime,
    pricingItems,
    schedules,
}: PackageProductItemProps) => {
    const { t } = useTranslation('product');
    const navigate = useNavigate();
    const pricingItem = getMinPriceFromDualCurrencyItems(pricingItems);
    const selectedTime = formatTimeOffset(startTime);

    const { hours, minutes = 0 } = schedules![0].duration.estimated;
    const duration = `${hours}${minutes ? '+' : ''}h`;

    return (
        <Card
            sx={{ width: 'auto', px: 3, pt: 2.5, pb: 3 }}
            buttons={
                <IconButtonGroup>
                    <IconButton
                        icon={<VisibilityIcon fontSize="small" />}
                        onClick={() =>
                            navigate(`/products/${productId}`, {
                                state: {
                                    source: 'package-product-item',
                                    readOnly: true,
                                },
                            })
                        }
                        tooltip={t('Preview')}
                    />
                </IconButtonGroup>
            }
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ mb: 1.5 }}
            >
                <Tag
                    label="Type"
                    values={[productType]}
                    sx={{ textTransform: 'capitalize' }}
                />
            </Stack>

            <Typography variant="h5" sx={{ fontWeight: 900, mb: 2.25 }}>
                {productName}
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <SquareCard
                    Icon={AccessTimeRounded}
                    title="Duration"
                    value={duration}
                    sx={{ minWidth: 'auto', width: '100%' }}
                />

                <SquareCard
                    Icon={PaymentsOutlined}
                    title="Price"
                    value={`${pricingItem.minPrice} ${pricingItem.currency}`}
                    sx={{ minWidth: 'auto', width: '100%' }}
                />

                <SquareCard
                    Icon={ScheduleRounded}
                    title="Start"
                    value={selectedTime}
                    sx={{ minWidth: 'auto', width: '100%' }}
                />
            </Stack>
        </Card>
    );
};
